import axios from "axios";
import { api } from "../urlConfig";
import store from "../store";
import { authConstants, accessGateConstants } from "../actions/constants";

const SITE_NAME = 'alotb.ru';

const PAID_ACCESS_CODES = new Set([
  'FEATURE_ACCESS_REQUIRED',
  'SUBSCRIPTION_REQUIRED',
  'OFFICE_SUBSCRIPTION_REQUIRED',
  'OFFICE_SUSPENDED',
  'OFFICE_3D_SUBSCRIPTION_REQUIRED',
  'OFFICE_GUEST_SCENE_SUBSCRIPTION_REQUIRED',
  'WAREHOUSE_SUBSCRIPTION_REQUIRED',
  'PRINT_FORM_SUBSCRIPTION_REQUIRED',
  'SCENE_PAYMENT_REQUIRED',
  'SCENE_ACCESS_REQUIRED',
  'SCENE_EDIT_ACCESS_EXPIRED',
  'OFFICE_BALANCE_REQUIRED',
  'OFFICE_SUBSCRIPTION_FUNDS_REQUIRED',
  'SCENE_BILLING_LIMIT_EXCEEDED',
  'USER_PRO_REQUIRED',
]);

const token = window.localStorage.getItem("token");

const axiosInstance = axios.create({
  baseURL: api,
  headers: {
    Authorization: token ? `Bearer ${token}` : "",
    siteName: SITE_NAME,
  },
});

const getErrorMessage = (data = {}, fallback = "") => {
  return String(
    data.message ||
    data.error ||
    data.description ||
    data.detail ||
    fallback ||
    "Доступ к функции ограничен"
  );
};

const inferAccessCode = ({ status, data = {}, requestUrl = "" }) => {
  const explicitCode = data.code || data.errorCode || data.error_code || data.type;

  if (explicitCode) return String(explicitCode);

  const url = String(requestUrl || "").toLowerCase();
  const message = getErrorMessage(data).toLowerCase();

  if (status === 402 || status === 403) {
    if (url.includes("/office/product-model/") || (message.includes("3d") && message.includes("подпис"))) {
      return "OFFICE_3D_SUBSCRIPTION_REQUIRED";
    }

    if (message.includes("склад") || message.includes("warehouse")) {
      return "WAREHOUSE_SUBSCRIPTION_REQUIRED";
    }

    if (message.includes("печать") || message.includes("print")) {
      return "PRINT_FORM_SUBSCRIPTION_REQUIRED";
    }

    if ((message.includes("сцен") || message.includes("scene")) && (message.includes("оплат") || message.includes("access"))) {
      return "SCENE_PAYMENT_REQUIRED";
    }

    if (message.includes("подпис")) {
      return "SUBSCRIPTION_REQUIRED";
    }
  }

  return explicitCode || null;
};

const getFeatureByCode = (code = "") => {
  if (code === "OFFICE_3D_SUBSCRIPTION_REQUIRED") return "office_3d";
  if (code === "OFFICE_GUEST_SCENE_SUBSCRIPTION_REQUIRED") return "office_3d";
  if (code === "WAREHOUSE_SUBSCRIPTION_REQUIRED") return "warehouse";
  if (code === "PRINT_FORM_SUBSCRIPTION_REQUIRED") return "print_form";
  if (["SCENE_PAYMENT_REQUIRED", "SCENE_ACCESS_REQUIRED", "SCENE_EDIT_ACCESS_EXPIRED", "SCENE_BILLING_LIMIT_EXCEEDED"].includes(code)) return "scene_access";
  if (code === "USER_PRO_REQUIRED") return "office_access";

  return "paid_feature";
};

const getScopeByCode = (code = "") => {
  if (code.includes("OFFICE") || code.includes("WAREHOUSE") || code.includes("3D")) return "office";
  if (code.includes("SCENE")) return "scene";
  if (code.includes("USER")) return "user";

  return "feature";
};

const extractProductIdFromUrl = (requestUrl = "") => {
  const match = String(requestUrl || "").match(/\/office\/product-model\/([^/?#]+)/i);

  return match ? decodeURIComponent(match[1]) : null;
};

const shouldOpenAccessGate = ({ status, code, data = {}, requestUrl = "" }) => {
  if (!status || status < 400) return false;
  if (PAID_ACCESS_CODES.has(code)) return true;

  const message = getErrorMessage(data).toLowerCase();

  if ((status === 402 || status === 403) && requestUrl.includes("/office/product-model/")) {
    return true;
  }

  if (status === 402 && message.includes("подпис")) {
    return true;
  }

  return false;
};

const buildAccessGatePayload = (error) => {
  const response = error?.response || {};
  const config = error?.config || {};
  const data = response.data || {};
  const status = response.status;
  const requestUrl = config.url || "";
  const params = config.params || {};

  const code = inferAccessCode({
    status,
    data,
    requestUrl,
  });

  if (!shouldOpenAccessGate({
    status,
    code,
    data,
    requestUrl,
  })) {
    return null;
  }

  const state = store.getState?.() || {};
  const officeId =
    data.officeId ||
    data.office?._id ||
    data.office?.id ||
    params.officeId ||
    state.office?.currentOfficeId ||
    null;

  const productId =
    data.productId ||
    params.productId ||
    extractProductIdFromUrl(requestUrl);

  const sceneId =
    data.sceneId ||
    data.salesSessionId ||
    params.sceneId ||
    params.salesSessionId ||
    null;

  return {
    code,
    status,
    feature: getFeatureByCode(code),
    scope: getScopeByCode(code),
    title: data.title || "",
    message: getErrorMessage(data),
    officeId: officeId && officeId !== "all" ? String(officeId) : null,
    userId: state.auth?.user?._id || state.auth?.user?.id || null,
    productId: productId ? String(productId) : null,
    sceneId: sceneId ? String(sceneId) : null,
    requestUrl,
    method: String(config.method || "get").toUpperCase(),
    payload: {
      response: data,
      requestParams: params,
      requestUrl,
      productId,
      sceneId,
    },
  };
};

axiosInstance.interceptors.request.use((req) => {
  const { auth } = store.getState();
  const sceneGuestToken = String(req.sceneGuestToken || '').trim();

  if (sceneGuestToken) {
    req.headers.Authorization = `Bearer ${sceneGuestToken}`;
  } else if (auth.token) {
    req.headers.Authorization = `Bearer ${auth.token}`;
  }

  return req;
});

axiosInstance.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error.response?.status;
    const message = getErrorMessage(error.response?.data || {}, error.message);

    const requestUrl = String(error?.config?.url || "");
    const responseCode = String(
      error?.response?.data?.code ||
      error?.response?.data?.errorCode ||
      ""
    ).toUpperCase();
    if (responseCode === "OFFICE_SUSPENDED" && typeof window !== "undefined") {
      const state = store.getState?.() || {};
      const data = error?.response?.data || {};
      const officeId = data.officeId || data.office?._id || data.office?.id || state.office?.currentOfficeId || null;

      window.dispatchEvent(new CustomEvent("office:suspended", {
        detail: { officeId: officeId && officeId !== "all" ? String(officeId) : null },
      }));
    }

    const isSceneGuestAccessError = responseCode.startsWith("SCENE_GUEST_");
    const isSceneGuestTokenRequest = (
      !!error?.config?.sceneGuestToken ||
      requestUrl.includes("/scene-guest-access/resolve") ||
      requestUrl.includes("/scene-guest-access/view") ||
      requestUrl.includes("/scene-guest-access/model/") ||
      requestUrl.includes("/scene-guest-access/screenshots")
    );
    const isSceneGuestViewerRequest = (
      isSceneGuestTokenRequest ||
      requestUrl.includes("/scene-guest-access/claim") ||
      requestUrl.includes("/scene-guest-access/my")
    );

    if (
      !isSceneGuestTokenRequest &&
      !isSceneGuestAccessError &&
      (status === 401 || message.toLowerCase().includes("jwt expired"))
    ) {
      localStorage.clear();

      store.dispatch({
        type: authConstants.LOGOUT_BY_SESSION_EXPIRED,
        payload: { message: "Сессия истекла" }
      });
    } else {
      const accessGatePayload = buildAccessGatePayload(error);

      const onOfficeWorkspace = typeof window !== "undefined" && window.location.pathname === "/adminLayout";
      const shouldShowGate = responseCode !== "OFFICE_SUSPENDED" || onOfficeWorkspace;

      if (accessGatePayload && !isSceneGuestViewerRequest && shouldShowGate) {
        store.dispatch({
          type: accessGateConstants.ACCESS_GATE_OPEN,
          payload: accessGatePayload,
        });
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
